// The table's sounds, computed (design 8 item 6, design 10 §8): no files to load, our own work, released CC0 with the client. Each is
// a sum of struck modes, from a model and not by ear: a thing struck for a contact of width `contact` seconds rings in each of its
// modes as much as the half-sine force of that contact holds at that frequency, so the softer the contact the duller the sound, and
// every number below says what it is. Rendered once, then played as a buffer: a phone does no synthesis during a game. No imports:
// scripts/sounds.mjs builds the listening page from this file alone.

export type Sound = "discard" | "discardFar" | "call" | "kan" | "riichi" | "win" | "winOther" | "turn" | "low" | "draw" | "over" | "ready" | "warning";

export const RATE = 32000; // the browser resamples; nothing here needs more than 8 kHz, and a third fewer samples than 48 kHz

/** A mode of something struck: Hz, how strongly the strike finds it, and the seconds its ring takes to fall by 1/e. */
type Mode = readonly [hz: number, gain: number, decay: number];

/** What a half-sine contact of `width` seconds holds at `hz`: 1 at low frequencies, falling away past 1/width. */
export function contactAt(hz: number, width: number): number {
  const x = 2 * hz * width;
  if (Math.abs(x - 1) < 1e-6) return Math.PI / 4;
  return Math.abs(Math.cos((Math.PI * x) / 2) / (1 - x * x));
}

/** Adds one strike at `at` seconds: each mode rings by a recurrence, two multiplications a sample. */
function strike(out: Float32Array, at: number, modes: readonly Mode[], width: number, level: number): void {
  const first = Math.round(at * RATE);
  for (const [hz, gain, decay] of modes) {
    const a = level * gain * contactAt(hz, width);
    const fall = Math.exp(-1 / (decay * RATE));
    const w = (2 * Math.PI * hz) / RATE;
    const c = 2 * fall * Math.cos(w);
    const ff = fall * fall;
    let y1 = 0;
    let y2 = -a * fall * Math.sin(w); // so that y(n) = a e^(-n/τ) sin(wn): it starts from silence, without a step
    for (let i = first + 1; i < out.length; i++) {
      const y = c * y1 - ff * y2;
      out[i] = out[i]! + y;
      y2 = y1;
      y1 = y;
    }
  }
}

// The mat and table under a tile: the low modes are the table's, which a phone's speaker cannot play; what it can is the mat's knock
// between 1 and 4 kHz. A tile is too small to ring in the audible range itself (its first mode is above 40 kHz): what is heard is
// what it strikes.
const TABLE: readonly Mode[] = [[210, 0.55, 0.03], [430, 0.5, 0.022], [790, 0.6, 0.016], [1340, 0.75, 0.012], [2250, 0.8, 0.008], [3600, 0.55, 0.005], [5400, 0.25, 0.003]];
// A bar of wood (a marimba's: its second mode tuned to four times the first), for what the game says rather than what the tiles do.
const wood = (hz: number, decay: number): Mode[] => [[hz, 1, decay], [hz * 3.93, 0.35, decay * 0.35], [hz * 9.2, 0.09, decay * 0.12]];
// A bar of metal (a glockenspiel's free bar, inharmonic), for the riichi: rings long, cuts through.
const metal = (hz: number, decay: number): Mode[] => [[hz, 1, decay], [hz * 2.756, 0.5, decay * 0.55], [hz * 5.404, 0.22, decay * 0.28]];

const NOTE = { G4: 392, A4: 440, C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880, B5: 987.77, C6: 1046.5, G6: 1567.98 };

/** How loud each is at the top of the volume (peak), so that they stand in the right order: what the player must not miss above what they will hear hundreds of times. */
const LEVEL: Record<Sound, number> = { discard: 0.62, discardFar: 0.36, turn: 0.36, low: 0.42, call: 0.5, kan: 0.55, draw: 0.35, warning: 0.4, riichi: 0.6, winOther: 0.4, win: 0.85, over: 0.55, ready: 0.9 };

const SECONDS: Record<Sound, number> = { discard: 0.11, discardFar: 0.11, turn: 0.28, low: 0.1, call: 0.34, kan: 0.42, draw: 0.5, warning: 0.34, riichi: 0.85, winOther: 0.5, win: 0.95, over: 1.05, ready: 1.0 };

/** One of a few takes of a tile set down: the same tile never lands twice alike, and a hand of them is not a machine gun. */
function setDown(out: Float32Array, take: number, contact: number): void {
  const shift = 1 + [0, 0.035, -0.03, 0.018][take % 4]!;
  const modes = TABLE.map(([hz, gain, decay]) => [hz * shift, gain, decay] as const);
  // It lands, rocks once onto its other edge, and settles: smaller and sooner each time.
  const rock = 0.0075 + 0.0015 * (take % 3);
  strike(out, 0, modes, contact, 1);
  strike(out, rock, modes, contact * 0.8, 0.34);
  strike(out, rock * 1.55, modes, contact * 0.8, 0.12);
}

function compute(sound: Sound, take: number): Float32Array {
  const out = new Float32Array(Math.round(SECONDS[sound] * RATE));
  const notes = (list: readonly (readonly [at: number, modes: Mode[]])[], width = 0.0009) => list.forEach(([at, m]) => strike(out, at, m, width, 1));
  switch (sound) {
    case "discard": // the contact of a tile placed firmly
      setDown(out, take, 0.00035);
      break;
    case "discardFar": // across the table: a softer contact, so duller as well as quieter
      setDown(out, take, 0.0007);
      break;
    case "turn":
      notes([[0, wood(NOTE.B5, 0.05)]]);
      break;
    case "low": // a tick, short enough not to cover the next
      notes([[0, wood(NOTE.G6, 0.016)]], 0.0005);
      break;
    case "call":
      notes([[0, wood(NOTE.C5, 0.07)], [0.09, wood(NOTE.G5, 0.08)]]);
      break;
    case "kan":
      notes([[0, wood(NOTE.C5, 0.07)], [0.08, wood(NOTE.E5, 0.07)], [0.16, wood(NOTE.G5, 0.09)]]);
      break;
    case "draw": // nobody won: falling, and quiet
      notes([[0, wood(NOTE.C5, 0.1)], [0.15, wood(NOTE.G4, 0.13)]]);
      break;
    case "warning": // something went wrong: the same note twice, low
      notes([[0, wood(NOTE.G4, 0.07)], [0.12, wood(NOTE.G4, 0.07)]]);
      break;
    case "riichi":
      notes([[0, metal(NOTE.E5 * 2, 0.26)]], 0.0002);
      break;
    case "winOther": // someone else's: said, not celebrated
      notes([[0, wood(NOTE.E5, 0.1)], [0.12, wood(NOTE.A4, 0.14)]]);
      break;
    case "win":
      notes([[0, wood(NOTE.C5, 0.14)], [0.09, wood(NOTE.E5, 0.14)], [0.18, wood(NOTE.G5, 0.14)]]);
      strike(out, 0.27, metal(NOTE.C6, 0.2), 0.0002, 0.8);
      break;
    case "over":
      notes([[0, wood(NOTE.G4, 0.17)], [0.16, wood(NOTE.C5, 0.17)], [0.32, wood(NOTE.E5, 0.22)]]);
      break;
    case "ready": // a ranked table has formed: two rising calls, hard to miss from another room
      [0, 0.32].forEach((at) => {
        strike(out, at, metal(NOTE.D5, 0.14), 0.0002, 1);
        strike(out, at + 0.15, metal(NOTE.A5, 0.16), 0.0002, 1);
      });
      break;
  }
  // Scaled to its level, and let down to nothing over its last 8 ms, so that no ring is cut off with a click.
  let peak = 0;
  for (const x of out) peak = Math.max(peak, Math.abs(x));
  const scale = peak > 0 ? LEVEL[sound] / peak : 0;
  const tail = Math.round(0.008 * RATE);
  for (let i = 0; i < out.length; i++) out[i] = out[i]! * scale * Math.min(1, (out.length - 1 - i) / tail);
  return out;
}

export const TAKES = 4; // of a tile set down

const made = new Map<string, Float32Array>();

/** The samples of `sound` at RATE, its peak LEVEL's, kept. */
export function samples(sound: Sound, take = 0): Float32Array {
  const key = `${sound}${sound === "discard" || sound === "discardFar" ? take % TAKES : 0}`;
  let s = made.get(key);
  if (!s) {
    s = compute(sound, take);
    made.set(key, s);
  }
  return s;
}
